import React, { useCallback, useEffect, useMemo, useState } from "react";

import DataGrid, {
  Column,
  ColumnChooser,
  DataGridRef,
  DataGridTypes,
  Editing,
  FilterBuilderPopup,
  FilterPanel,
  FilterRow,
  GroupPanel,
  HeaderFilter,
  Item,
  LoadPanel,
  Paging,
  Scrolling,
  SearchPanel,
  Selection,
  StateStoring,
  Toolbar,
} from "devextreme-react/data-grid";
import { ProgressBar } from "devextreme-react/progress-bar";
import "devextreme-react/text-area";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { run } from "effection";
import sum from "hash-sum";
import { isEqual } from "lodash";
import { useDebouncedCallback } from "use-debounce";

import {
  columnChooserButtonOpts,
  exportToPdfOpts,
} from "@app/components/lists/lxr_common_elements";
import { usePdfContext, useWindowDimensions } from "@app/contexts";
import eventEmitter from "@app/contexts/emitter";
// import { cancelDbSyncPoll, pollWhileOpened } from '@app/statefx/xThunks/ds';
import { useComponentStack } from "@app/contexts/useComponentStack";
// import { setGroupData } from '@app/state';
import {
  CONST_SHOW_SPVMSG_POPUP,
  EComponentStack,
  PERSISTOR_NAME,
} from "@app/state/constants";
// import { uiStateAdapter } from '@app/stateCommon/idbAdapters';
import { useFxDispatch, useFxSelector } from "@app/statefx";
import { sendStorageRequest } from "@app/statefx/gridpersistor";
import { isOk } from "@app/statefx/helpers";
import {
  parseDateCreareSafe,
  parseDateCreareSafe2,
  parseDateCreareSafe3,
} from "@app/statefx/helpers/invoicedate";
import { schema } from "@app/statefx/schema";
import { select_apiProf_issuersArray } from "@app/statefx/selectors/invoiceIssuers.sel";
import { selectSpvDataOverall } from "@app/statefx/selectors/invoiceOverall.sel";
import { selectAreUnknonwContractors } from "@app/statefx/selectors/uiSpvMain.sel";
import { fxTriggerSPVMessageReading } from "@app/statefx/xThunks/prc-spvmsg";
import {
  fxFixUnknownContractors,
  fxFixUnknownContractorsNJS,
  fxUiUpdateSpvGrid,
  pushChanges,
  setCurrentSpvMessage,
} from "@app/statefx/xThunks/uiSpvMain";
import "@app/style/dx.generic.catetory-chooser-compact.min.css";

import "./invoices.scss";

import type {
  TSPVMsgReadState,
  TSPVMsgMarkedState,
} from "@app/types/insider/invoice";
const readStateValues = ["READ_OK", "READ_NOT_OK", "NOT_READ"];
const markedStateValues = ["RED", "GREEN", "BLUE", "YELLOW", "NO_MARK"];
const coloredFlags = [
  {
    id: 104,
    key: "RED",
    text: "semn rosu",
    icon: "dx-icon dx-icon-bookmark red",
  },
  {
    id: 105,
    key: "GREEN",
    text: "semn verde",
    icon: "dx-icon dx-icon-bookmark green",
  },
  {
    id: 106,
    key: "BLUE",
    text: "semn albastru",
    icon: "dx-icon dx-icon-bookmark blue",
  },
  {
    id: 107,
    key: "YELLOW",
    text: "semn galben",
    icon: "dx-icon dx-icon-bookmark yellow",
  },
  // no category
  {
    id: 108,
    key: "NO_MARK",
    text: "fara semn",
    icon: "dx-icon dx-icon-bookmark empty",
  },
] as TContextItem[];

const readFlags = [
  {
    id: 1,
    key: "READ_OK", // 'MARK_AS_READOK
    text: "vazut, ok",
    icon: "dx-icon dx-icon-check green",
  },
  {
    id: 2,
    key: "READ_NOT_OK", // 'MARK_AS_READNOTOK
    text: "vazut, probleme",
    icon: "dx-icon dx-icon-errorcircle red",
  },
  {
    id: 3,
    key: "NOT_READ", // 'MARK_AS_NOTREAD
    text: "marcat ca necitit",
  },
] as TContextItem[];

const filterBuilderPopupPosition = {
  of: window,
  at: "top" as const,
  my: "top" as const,
  offset: { y: 10 },
};

type TContextItem = {
  id: number;
  key: TSPVMsgMarkedState | TSPVMsgReadState;
  text: string;
  icon: string;
  onClick?: (e: any) => void;
};

const db = schema;

const calculateCreateValue = (e) =>
  e?.QDATA_CREARE ? parseDateCreareSafe(e.QDATA_CREARE) : null;
const calculateDisplayValue = (e) =>
  e?.QDATA_CREARE
    ? parseDateCreareSafe2(e.QDATA_CREARE).toLocaleString()
    : null;
const calculateDisplayValue3 = (e) =>
  e?.QDATA_CREARE
    ? parseDateCreareSafe3(e.QDATA_CREARE).toLocaleString()
    : null;
const calculateCititDisplayValue = (e) =>
  e?.CITIT_LA ? new Date(e.CITIT_LA).toLocaleString() : null;

const markedHeaderFilter = {
  dataSource: {
    store: {
      type: "array",
      data: ["RED", "GREEN", "BLUE", "YELLOW", "NO_MARK"],
    },
    map: function (item) {
      switch (item) {
        case "RED":
          return { text: "Rosu", value: item };
        case "GREEN":
          return { text: "Verde", value: item };
        case "BLUE":
          return { text: "Albastru", value: item };
        case "YELLOW":
          return { text: "Galben", value: item };
        default:
          return { text: "Fara semn", value: item };
      }
    },
  },
};

const readStateHeaderFilter = {
  dataSource: {
    store: {
      type: "array",
      data: ["READ_OK", "READ_NOT_OK", "NOT_READ"],
    },
    map: function (item) {
      switch (item) {
        case "READ_OK":
          return { text: "Vazut, ok", value: item };
        case "READ_NOT_OK":
          return { text: "Vazut, probleme", value: item };
        default:
          return { text: "Necitit", value: item };
      }
    },
  },
};

const readStateRender = (e) => {
  switch (e.value) {
    case "READ_OK":
      return (
        <i key={`irs${e.key}`} className={`dx-icon dx-icon-check green`}></i>
      );
    case "READ_NOT_OK":
      return (
        <i
          key={`irs${e.key}`}
          className={`dx-icon dx-icon-errorcircle red`}
        ></i>
      );
    case "NOT_READ":
    default:
      return (
        <i key={`irs${e.key}`} className={`dx-icon dx-icon-check empty`}></i>
      );
  }
};
const markedStateRender = (e) => {
  switch (e.value) {
    case "RED":
      return (
        <i key={`msr${e.key}`} className={`dx-icon dx-icon-bookmark red`}></i>
      );
    case "GREEN":
      return (
        <i key={`msr${e.key}`} className={`dx-icon dx-icon-bookmark green`}></i>
      );
    case "BLUE":
      return (
        <i key={`msr${e.key}`} className={`dx-icon dx-icon-bookmark blue`}></i>
      );
    case "YELLOW":
      return (
        <i
          key={`msr${e.key}`}
          className={`dx-icon dx-icon-bookmark yellow`}
        ></i>
      );
    case "NO_MARK":
    default:
      return (
        <i key={`msr${e.key}`} className={`dx-icon dx-icon-bookmark empty`}></i>
      );
  }
};

/* -------------------------------------------------------------------------- */
const SmallBootLoader = () => {
  const text = useFxSelector(schema.loaderBootText.select);
  const processed = useFxSelector(schema.loaderBootProgress.select);
  const elementAttr = {
    class: "progress-bar-status",
  };
  const statusFormat = (value: number) => {
    return `${Number(processed).toFixed(2)}% | ${text || "indexare"}`;
  };
  return (
    <div className="small-boot-loader">
      <ProgressBar
        id="progress-bar-status"
        className={Math.ceil(processed) === 100 ? "complete" : ""}
        min={0}
        max={100}
        elementAttr={elementAttr}
        statusFormat={statusFormat}
        value={processed}
      />
    </div>
  );
};

const idbName = PERSISTOR_NAME;
/* -------------------------------------------------------------------------- */
export const SPVMessages = () => {
  useComponentStack(EComponentStack.SpvMessages);
  const fxDispatch = useFxDispatch();
  const { anyListGridRef: tsGridRef, openPdf } = usePdfContext();
  const [selectionMode, setSelectionMode] = useState<"single" | "multiple">(
    "single"
  );
  // const idbName_ = useFxSelector(schema.app.select);
  // const idbName = useMemo(() => `m.${idbName_.idb}`, [idbName_]);

  const areUnkownContractors = useFxSelector(selectAreUnknonwContractors);
  const { height: fullHeight, headersFooters } = useWindowDimensions();
  const dataRaw = useFxSelector(selectSpvDataOverall);
  const [data, setData] = useState(dataRaw);
  const issuers = useFxSelector(select_apiProf_issuersArray);
  const theToolbarState = useFxSelector(schema.uiInvoiceOverall.select);

  const lock = useFxSelector(schema.lock.select);
  const isBooting = useMemo(() => (lock || "").startsWith("$:bootup"), [lock]);

  const sumDataRaw = useMemo(() => {
    return sum(dataRaw);
  }, [dataRaw]);

  const selectedCIF = useMemo(
    () => theToolbarState.selectedCIF,
    [theToolbarState]
  );

  const dataSource = new DataSource({
    store: new CustomStore({
      loadMode: "raw",
      key: "FXM_ID",

      load: () => {
        return data;
      },
      insert: async (values) => {
        return Promise.resolve(values);
      },
      // update: async (key, values) => {
      //   console.log("update", key);
      //   return Promise.resolve(values);
      // },
      // onUpdating: (key, values) => {
      //   console.log("onUpdating", key);
      //   return Promise.resolve({ [key]: values });
      // },
      // onUpdated: (key, values) => {
      //   return Promise.resolve(values);
      // },
      remove: (key) => {
        return Promise.resolve(key);
      },
    }),
    reshapeOnPush: true,
  });

  const customShowColumnChooser = useCallback((e) => {
    tsGridRef.current.instance().showColumnChooser();
    const columnChooserView = (
      tsGridRef.current.instance as unknown as typeof e.component
    ).getView("columnChooserView");
    if (!columnChooserView._popupContainer) {
      columnChooserView._initializePopupContainer();
      columnChooserView.render();
    }
    columnChooserView._popupContainer.option("position", {
      of: tsGridRef.current.instance().element(),
      my: "right top",
      at: "right top",
      offset: "-20 85",
    });
  }, []);

  const onEmitentChanged = (e: any) => {
    const { selectedItem } = e;
    if (selectedItem) {
      fxDispatch(
        fxUiUpdateSpvGrid({ key: "selectedCIF", value: selectedItem.CIF })
      );
    }
  };

  const selectionChanged = (e: any) => {
    const { selectedRowsData } = e;
    if (selectedRowsData.length > 0) {
      fxDispatch(setCurrentSpvMessage(selectedRowsData[0]));
    }
  };
  const rowPrepared = (e: any) => {
    if (e.rowType === "header") {
      e.rowElement.style.height = "60px";
    } else if (e.rowType === "data") {
      if (e.data.IS_TEMPORARY) {
        e.rowElement.classList.add("with-tempdata");
      }
    }
  };
  const selectionOptions = useMemo(
    () => ({
      keyExpr: "key",
      selectedItemKeys: [selectionMode],
      items: [
        {
          icon: "orderedlist",
          key: "single",
          text: "",
          onClick: () => {
            setSelectionMode("single");
          },
          hint: "Selectia unui singur mesaj | Operatii pe mesajul selectat",
        },
        {
          icon: "checklist",
          key: "multiple",
          text: "",
          onClick: () => {
            setSelectionMode("multiple");
          },
          hint: "Selectia multipla de mesaje | Operatii pe mesajele selectate",
        },
      ],
    }),
    [selectionMode]
  );

  const readMessagesOptions = {
    icon: "refresh",
    text: "Mesaje noi",
    stylingMode: "contained",
    type: "default",
    onClick: () => {
      eventEmitter.emit(CONST_SHOW_SPVMSG_POPUP, "start");
      fxDispatch(fxTriggerSPVMessageReading(selectedCIF));
    },
  };

  const taskReadContractorInfoOptions = useMemo(
    () =>
      ({
        icon: "accountbox",
        text: "Completare",
        stylingMode: "outlined",
        type: "normal",
        onClick: () => {
          process.env.REACT_APP_MODE === "DELPHI"
            ? fxDispatch(fxFixUnknownContractors())
            : fxDispatch(fxFixUnknownContractorsNJS());
        },
        visible: false,
      } as any),
    [areUnkownContractors]
  );

  const exportGridToPdf = useCallback(() => {
    openPdf({
      //[##findme410##]
      filename: `Mesaje_SPV.pdf`,
      showReportHeader: true,
      layoutParam: "p",
    });
  }, []);

  const contextMenuPreparing = (e: any) => {
    if (!e.row) return;
    try {
      if (e.column.dataField === "MARKED_STATE") {
        e.items = coloredFlags as TContextItem[];
      }
      if (e.column.dataField === "READ_STATE") {
        e.items = readFlags as TContextItem[];
      }
    } catch (error) {
      console.log("error #1001: ", error, e);
      e.items = [];
    }
    if (!e.items) return;
    e.items.map((item) => {
      item.onClick = (f) => {
        if (!tsGridRef?.current?.instance) return;
        if (e.row.rowType === "data") {
          tsGridRef.current
            .instance()
            .cellValue(e.row.rowIndex, e.column.dataField, item.key);
          tsGridRef.current.instance().saveEditData();
        }
      };
    });
  };

  const loadState = useCallback(() => {
    return sendStorageRequest("uiPersistSpvList", "GET");
  }, [idbName]);

  const saveState = useCallback(
    (state) => {
      sendStorageRequest("uiPersistSpvList", "PUT", state);
    },
    [idbName]
  );

  async function sendBatchRequest(
    url: string,
    changes: DataGridTypes.DataChange[]
  ) {
    fxDispatch(pushChanges(changes));
  }

  async function processBatchRequest(
    url: string,
    changes: DataGridTypes.DataChange[],
    component: DataGridRef
  ) {
    await sendBatchRequest("nop", changes);
    // await component.refresh(true); // <<< this looks like a flicker
    // console.log("processBatchRequest, cancelling", changes);
    component.instance().cancelEditData();
  }
  const onSaving = (e: DataGridTypes.SavingEvent) => {
    e.cancel = true;
    if (e.changes.length) {
      e.promise = processBatchRequest(`${URL}/Batch`, e.changes, e.component);
    }
  };

  const debouncedSaveState = useDebouncedCallback((e) => {
    tsGridRef.current.instance().saveEditData();
  }, 300);

  const flagCellClick = (e) => {
    const updateState = (stateValues: string[]) => {
      const idx = stateValues.indexOf(e.data[e.column.dataField]);
      const newIdx = (idx + 1) % stateValues.length;

      tsGridRef.current
        .instance()
        .cellValue(e.rowIndex, e.column.dataField, stateValues[newIdx]);

      const newRecord = {
        ...e.data,
        [e.column.dataField]: stateValues[newIdx],
      };

      const recKey = e.data.FXM_ID;
      const newdata = data.map((record) =>
        record.FXM_ID === recKey ? newRecord : record
      );
      setData(newdata);
      debouncedSaveState(e);
    };

    switch (e.column.dataField) {
      case "READ_STATE":
        updateState(readStateValues);
        break;
      case "MARKED_STATE":
        updateState(markedStateValues);
        break;
    }
  };

  // const initialize = useCallback((e) => {
  //   tsGridRef.current = e;
  // }, []);

  // THE POLLER //
  // useEffect(() => {
  //   console.log('pollWhileOpened');
  //   sagaDispatch({ type: 'POLLING_TEARDOWN' });
  //   fxDispatch(cancelDbSyncPoll());
  //   fxDispatch(pollWhileOpened());
  //   return () => {
  //     console.log('pollWhileOpened cleanup');
  //     fxDispatch(cancelDbSyncPoll());
  //   };
  // }, []);

  useEffect(() => {
    // for fast update on rowclick
    // if (!isEqual(sumDataRaw, sum(data))) {
    //   console.log('sum(dataRaw)', sum(data))
    //   console.log('sumDataRaw', sumDataRaw)
    //   setData(dataRaw);
    // }
    setData(dataRaw);
  }, [sumDataRaw]);
  return (
    <DataGrid
      id="ivSpvGrid"
      className="dx-swatch-catetory-chooser-compact"
      ref={tsGridRef}
      height={fullHeight - headersFooters}
      dataSource={dataSource}
      showBorders={true}
      showRowLines={true}
      showColumnLines={true}
      columnAutoWidth={false}
      allowColumnReordering={true}
      allowColumnResizing={true}
      autoNavigateToFocusedRow={true}
      focusedRowEnabled={false}
      filterSyncEnabled={true}
      repaintChangesOnly={true}
      remoteOperations={{
        filtering: false,
      }}
      renderAsync={false}
      wordWrapEnabled={true}
      noDataText={""}
      onRowPrepared={rowPrepared}
      onSelectionChanged={selectionChanged}
      onContextMenuPreparing={contextMenuPreparing}
      onCellClick={flagCellClick}
      onSaving={onSaving}
    >
      <Paging enabled={false} />
      <Selection mode={selectionMode} showCheckBoxesMode="always" />
      <ColumnChooser enabled={true} />
      <SearchPanel visible={true} />
      <HeaderFilter visible={true} />
      <FilterRow visible={true} />
      <FilterPanel visible={true} />
      <FilterBuilderPopup position={filterBuilderPopupPosition} />
      <LoadPanel enabled={false} showPane={false} showIndicator={true} />
      {/* <Grouping
			autoExpandAll={true}
			contextMenuEnabled={false}
			expandMode="rowClick"
		  /> */}
      <Editing
        mode="cell"
        allowUpdating={true}
        allowDeleting={false}
        allowAdding={false}
        useIcons={false}
      />
      <StateStoring
        enabled={true}
        type="custom"
        customLoad={loadState}
        customSave={saveState}
      />
      <GroupPanel visible={false} allowColumnDragging={true} />
      <Toolbar>
        <Item
          location="before"
          widget="dxSelectBox"
          options={{
            id: "emitentselect",
            type: "text",
            elementAttr: {
              class: `toolbar-picker ${selectedCIF ? "" : "dx-state-invalid"}`,
            },
            invalidMessage: "Selectati emitentul",
            dataSource: issuers,
            displayExpr: "DENUMIRE",
            valueExpr: "CIF",
            value: selectedCIF,
            onSelectionChanged: onEmitentChanged,
          }}
        />
        <Item
          location="before"
          visible={false} //we will use the tab panel in the right side
          widget="dxButtonGroup"
          options={selectionOptions}
        />
        <Item location="center" name="groupPanel" visible={false} />
        <Item location="center" name="searchPanel" visible={true} />
        <Item {...exportToPdfOpts({ exportGridToPdf })} />

        <Item
          location="after"
          widget="dxButton"
          options={readMessagesOptions}
          visible={!isBooting}
        />
        <Item
          location="after"
          widget="dxButton"
          options={taskReadContractorInfoOptions}
          visible={false} // idk what this is
        />
        <Item
          {...columnChooserButtonOpts({ customShowColumnChooser })}
          location="after"
          visible={!isBooting}
        />
        <Item location="after" render={SmallBootLoader} visible={isBooting} />
      </Toolbar>
      <Column
        dataField={"FXM_ID"}
        dataType={"number"}
        caption={"ID"}
        width={50}
        visible={false}
        allowEditing={false}
      />
      <Column
        dataField={"QDATA_CREARE"}
        name="QDATA_CREARE0"
        dataType={"date"}
        calculateCellValue={calculateCreateValue}
        // format={'yyyy-MM-dd HH:mm'}
        calculateDisplayValue={calculateDisplayValue}
        calculateSortValue={calculateCreateValue}
        caption={"Data creării (spv)"}
        width={100}
        defaultSortIndex={0}
        defaultSortOrder="asc"
        allowEditing={false}
      />
      <Column
        dataField={"QDATA_CREARE"}
        name="QDATA_CREARE1"
        dataType={"date"}
        calculateCellValue={calculateCreateValue}
        // format={'yyyy-MM-dd HH:mm'}
        calculateDisplayValue={calculateDisplayValue3}
        caption={"Ora creării (spv)"}
        width={90}
        visible={false}
        allowEditing={false}
      />

      <Column
        dataField={"QRESULT_TYPE"}
        dataType={"string"}
        caption={"Tip rezultat (spv)"}
        width={150}
        allowEditing={false}
      />
      <Column
        dataField={"QDETAILS"}
        dataType={"string"}
        caption={"Detalii (spv)"}
        // width={'45%'}
        visible={false}
        showInColumnChooser={true}
        allowEditing={false}
      />

      <Column
        dataField={"QMY_CIF"}
        dataType={"string"}
        caption={"CIF interogare (spv)"}
        width={90}
        visible={false}
        showInColumnChooser={true}
        allowEditing={false}
      />

      <Column
        dataField={"REFERINTA_INTERNA"}
        dataType={"string"}
        caption={"Referinta interna (Jurnal)"}
        width={100}
        visible={true}
        showInColumnChooser={true}
        allowEditing={false}
      />

      <Column
        dataField={"DENUMIRE_PARTENER"}
        dataType={"string"}
        caption={"Denumire partener  (Jurnal)"}
        // width={'auto'}
        visible={true}
        showInColumnChooser={true}
        allowEditing={false}
      />

      <Column
        dataField={"MARKED_STATE"}
        allowSorting={true}
        allowFiltering={true}
        allowEditing={false}
        allowResizing={false}
        allowHeaderFiltering={true}
        allowExporting={false}
        allowSearch={true}
        alignment="center"
        dataType={"string"}
        caption={"Cat."}
        width={40}
        visible={true}
        showInColumnChooser={true}
        cellRender={markedStateRender}
        editCellRender={markedStateRender}
        // headerFilter={markedHeaderFilter}
      />
      <Column
        dataField={"READ_STATE"}
        caption={"Viz."}
        allowSorting={true}
        allowFiltering={true}
        allowEditing={false}
        allowResizing={false}
        allowHeaderFiltering={true}
        allowExporting={false}
        allowSearch={true}
        alignment="center"
        dataType={"string"}
        width={40}
        visible={true}
        showInColumnChooser={true}
        cellRender={readStateRender}
        editCellRender={readStateRender}
        // headerFilter={readStateHeaderFilter}
      />

      <Column
        dataField={"QREQUEST_ID"}
        dataType={"string"}
        caption={"ID cerere (spv)"}
        width={100}
        visible={false}
        showInColumnChooser={true}
        allowEditing={false}
      />

      <Column
        dataField={"QRESPONSE_ID"}
        dataType={"string"}
        caption={"ID raspuns (spv)"}
        width={100}
        visible={false}
        showInColumnChooser={true}
        allowEditing={false}
      />

      <Column
        dataField={"NOTES"}
        dataType={"string"}
        caption={"Note"}
        width={100}
        visible={false}
        showInColumnChooser={true}
      />
      <Column
        dataField={"CITIT_LA"}
        dataType={"date"}
        calculateDisplayValue={calculateCititDisplayValue}
        caption={"Citit la"}
        visible={false}
        width={90}
        allowEditing={false}
      />
      <Column
        dataField={"CITIT_DE"}
        dataType={"number"}
        caption={"Citit de"}
        width={50}
        visible={false}
        showInColumnChooser={true}
        allowEditing={false}
      />
      <Column
        dataField={"MATCHED_EMITENT"}
        dataType={"string"}
        caption={"CIF Emitent ( Jurnal, cf.explicații)"}
        width={100}
        visible={false}
        showInColumnChooser={true}
        allowEditing={false}
      />
      <Column
        dataField={"MATCHED_BENEFICIAR"}
        dataType={"string"}
        caption={"Beneficiar (Jurnal, cf.explicații)"}
        width={100}
        visible={false}
        showInColumnChooser={true}
        allowEditing={false}
      />
    </DataGrid>
  );
};
//todo: a default period for the spv-messages to be read, also a selectbox for the period
